import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import '../superadmin/AddDistrict.css';
import './ParentFormsDashboard.css';
import { getFrInfoDocUrl } from '../../config/districtDocs';

const ParentFormsDashboard = () => {
  const [schoolLunchSubmission, setSchoolLunchSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  // Migrant questionnaire is required by the state once per student, so it is listed
  // per student with its own status rather than as a single household form.
  const [migrantStudents, setMigrantStudents] = useState([]);
  /**
   * Enrolment status per child, so this page can route into it.
   *
   * This page is called "View Forms", so it is where a parent looks when told to check for more
   * forms - and until now it listed only the household-level lunch application and the state
   * questionnaire. A family that completed both saw two green ticks and nothing else, and
   * concluded there was nothing left to do. The enrolment packet lives under My Students, one
   * menu item away, which is not a place anyone guesses.
   */
  const [enrollmentStudents, setEnrollmentStudents] = useState([]);

  useEffect(() => {
    fetchSchoolLunchData();
    fetchMigrantStatus();
    fetchEnrollmentStatus();
  }, []);

  const fetchMigrantStatus = async () => {
    try {
      const res = await axios.get('/api/migrant-questionnaire/status');
      setMigrantStudents(res.data.students || []);
    } catch (error) {
      // Non-fatal: the rest of the dashboard still works.
      console.error('Failed to load migrant questionnaire status:', error);
    }
  };

  const fetchEnrollmentStatus = async () => {
    try {
      const res = await axios.get('/api/student-intake/my-intake');
      setEnrollmentStudents(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      // Non-fatal, like the migrant lookup: the rest of the page still works and My Students
      // remains reachable from the menu.
      console.error('Failed to load enrollment status:', error);
    }
  };

  /**
   * What this child needs next, and where that lives.
   *
   * Mirrors the three states the My Students list shows. It deliberately does NOT reproduce that
   * screen's returning-student branch: that flow is being retired, and a second copy of the rule
   * is how the grade and form-name rules in this codebase drifted apart in the first place. A
   * returning student simply lands on the same enrolment screen, which is where My Students
   * would have sent them anyway once the flow is gone.
   */
  const enrollmentAction = (student) => {
    const owed = Number(student.forms_owed_count);
    const held = Number(student.forms_held_count);
    if (held > 0 && owed > 0 && student.has_core_form === false) {
      return {
        label: `Finish Forms (${owed} remaining)`,
        to: `/parent/edit-student/${student.id}`,
        tone: 'warn',
        note: 'Some forms are done - only what is missing is left'
      };
    }
    if (student.form_status === 'completed') {
      return {
        label: 'Edit Submission',
        to: `/parent/edit-student/${student.id}`,
        tone: 'done',
        note: '✓ enrollment complete'
      };
    }
    if (student.form_status === 'partial') {
      return {
        label: 'Continue Enrollment',
        to: `/parent/new-student/${student.id}`,
        tone: 'warn',
        note: 'Started, not yet submitted'
      };
    }
    return {
      label: 'Start Enrollment',
      to: `/parent/new-student/${student.id}`,
      tone: 'todo',
      note: 'Not started'
    };
  };

  const fetchSchoolLunchData = async () => {
    try {
      setLoading(true);

      // Dedicated endpoint rather than /progress?include_completed=true. That returns only the
      // 10 most recent submissions across every form type, so a family with several children
      // could have their lunch application fall outside it - and this card would then offer
      // "Start Application" to someone who had already applied.
      const lunchResponse = await axios.get('/api/forms/school-lunch-application/my-latest');
      if (lunchResponse.data) {
        setSchoolLunchSubmission(lunchResponse.data);
      }
    } catch (error) {
      console.error('Failed to fetch school lunch form data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSchoolLunch = async () => {
    if (!schoolLunchSubmission) return;

    const confirmed = window.confirm(
      'Are you sure you want to delete your school lunch application? This action cannot be undone.'
    );

    if (!confirmed) return;

    try {
      const submissionId = schoolLunchSubmission.id;
      await axios.delete(`/api/form-submissions/${submissionId}`);
      setSchoolLunchSubmission(null); // Reset state to show "Start Application" again
    } catch (error) {
      console.error('Failed to delete school lunch submission:', error);
      alert('Failed to delete school lunch application. Please try again.');
    }
  };

  return (
    <div className="parent-forms-dashboard">
      <h2>Back to School Forms</h2>

      <div className="form-grid">
        {/* Add New Student Form */}
        <div className="form-card">
          <h3>Add a New Student</h3>
          <p>Register a new student with basic information. Forms can be completed later from My Students.</p>

          <Link
            to="/parent/student-intake/new"
            className="btn-primary"
            style={{ width: 'fit-content' }}
          >
            Add New Student
          </Link>
        </div>

        {/* Enrolment, per child. Listed FIRST and before the household forms: a parent who
            completed the lunch application and the questionnaire has done the easy two and
            still has the long one ahead of them, and this is the page they come back to. */}
        {enrollmentStudents.length > 0 && (
          <div className="form-card">
            <h3>Enrollment Forms</h3>
            <p>
              Each student needs their own enrollment forms. The school lunch application and the
              migrant questionnaire below are separate &mdash; completing those does not complete
              enrollment.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {enrollmentStudents.map((student) => {
                const action = enrollmentAction(student);
                const name = [student.first_name, student.last_name].filter(Boolean).join(' ') || 'Student';
                const toneColor = action.tone === 'done'
                  ? '#155724'
                  : (action.tone === 'warn' ? '#8a6d00' : '#7b8794');
                return (
                  <div
                    key={student.id}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      gap: '0.75rem', flexWrap: 'wrap',
                      padding: '0.6rem 0.75rem', border: '1px solid #e3e8ee', borderRadius: '4px'
                    }}
                  >
                    <span>
                      <strong>{name}</strong>
                      {student.grade ? <span style={{ color: '#7b8794' }}> &middot; Grade {student.grade}</span> : null}
                      <span style={{ color: toneColor, marginLeft: '0.5rem', fontSize: '0.9rem' }}>
                        {action.note}
                      </span>
                    </span>
                    <Link
                      to={action.to}
                      className={action.tone === 'done' ? 'btn-secondary' : 'btn-primary'}
                      style={{
                        width: 'fit-content',
                        textDecoration: 'none',
                        padding: '0.5rem 1rem',
                        borderRadius: '4px',
                        display: 'inline-block',
                        ...(action.tone === 'done' ? { background: '#6c757d', color: 'white' } : {}),
                        ...(action.tone === 'warn' ? { background: '#ffc107', color: '#212529' } : {})
                      }}
                    >
                      {action.label}
                    </Link>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* School Lunch Application */}
        <div className="form-card">
          <h3>School Lunch Application</h3>
          <p>Apply for free or reduced price school meals for your family.</p>

          {getFrInfoDocUrl() && (
            <p style={{ margin: '0 0 0.75rem' }}>
              📄{' '}
              <a
                href={getFrInfoDocUrl()}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#2c6ca0', fontWeight: 600 }}
              >
                Free &amp; Reduced Price Meals Information (PDF)
              </a>
            </p>
          )}

          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {schoolLunchSubmission ? (
              <>
                <button
                  onClick={async () => {
                    try {
                      const response = await axios.post('/api/forms/school-lunch-application/generate-pdf',
                        { submissionId: schoolLunchSubmission.id },
                        { responseType: 'blob' }
                      );
                      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                      window.open(url, '_blank');
                    } catch (error) {
                      alert('Failed to generate PDF: ' + (error.response?.data?.error || error.message));
                    }
                  }}
                  className="btn-primary"
                  style={{ width: 'fit-content' }}
                >
                  View PDF
                </button>
                {/*
                  Was "Restart Application", which read as destructive: a parent wanting to add
                  a household member they had left off saw "Restart" beside "Delete" and
                  reasonably feared losing what they had filed. The form is append-only - a new
                  submission supersedes the old one and the old one stays on file - so this is
                  an update, and it now says so. The form prefills from the last submission.
                */}
                <Link
                  to="/parent/school-lunch/school-lunch-application"
                  className="btn-secondary"
                  style={{
                    width: 'fit-content',
                    background: '#3498db',
                    color: 'white',
                    textDecoration: 'none',
                    padding: '0.5rem 1rem',
                    borderRadius: '4px',
                    display: 'inline-block'
                  }}
                >
                  Submit an Updated Application
                </Link>
                <button
                  onClick={handleDeleteSchoolLunch}
                  className="btn-danger"
                  style={{
                    width: 'fit-content',
                    background: '#dc3545',
                    color: 'white',
                    border: 'none',
                    padding: '0.5rem 1rem',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Delete Application
                </button>
              </>
            ) : (
              <Link
                to="/parent/school-lunch/school-lunch-application"
                className="btn-primary"
                style={{ width: 'fit-content' }}
              >
                Start Application
              </Link>
            )}
          </div>

          {schoolLunchSubmission && (
            <div style={{
              marginTop: '1rem',
              padding: '0.75rem',
              background: '#d4edda',
              border: '1px solid #c3e6cb',
              borderRadius: '4px',
              fontSize: '0.9rem',
              color: '#155724'
            }}>
              <strong>✓ Application Submitted</strong>
              <br />
              Submitted on {new Date(schoolLunchSubmission.submitted_at).toLocaleDateString()}
              <br />
              <span style={{ display: 'inline-block', marginTop: '0.4rem' }}>
                Need to change something &mdash; add a household member, or update your income?
                Choose <strong>Submit an Updated Application</strong>. It opens prefilled with
                your answers so you only change what is different, and you sign it again. Your
                school uses the newest version.
              </span>
            </div>
          )}
        </div>

        {/* Migrant Education Parent Questionnaire — required by Missouri DESE at
            enrollment for every student, so it is listed once per student. */}
        {migrantStudents.length > 0 && (
          <div className="form-card">
            <h3>Migrant Questionnaire</h3>
            <p>
              Required by the state for each student at enrollment. Most families answer “no” to all
              three questions — it only takes a moment. Available in English, Español and Français.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {migrantStudents.map((s) => (
                <div
                  key={s.studentIntakeId}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    gap: '0.75rem', flexWrap: 'wrap',
                    padding: '0.6rem 0.75rem', border: '1px solid #e3e8ee', borderRadius: '4px'
                  }}
                >
                  <span>
                    <strong>{s.studentName || 'Student'}</strong>
                    {s.grade ? <span style={{ color: '#7b8794' }}> · Grade {s.grade}</span> : null}
                    {s.submittedAt && (
                      <span style={{ color: '#155724', marginLeft: '0.5rem', fontSize: '0.9rem' }}>
                        ✓ completed {new Date(s.submittedAt).toLocaleDateString()}
                      </span>
                    )}
                  </span>
                  <Link
                    to={`/parent/migrant-questionnaire/${s.studentIntakeId}`}
                    className={s.submittedAt ? 'btn-secondary' : 'btn-primary'}
                    style={{
                      width: 'fit-content',
                      textDecoration: 'none',
                      padding: '0.5rem 1rem',
                      borderRadius: '4px',
                      display: 'inline-block',
                      ...(s.submittedAt
                        ? { background: '#6c757d', color: 'white' }
                        : {})
                    }}
                  >
                    {s.submittedAt ? 'View' : 'Start Questionnaire'}
                  </Link>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default ParentFormsDashboard;



